import React, { useMemo } from 'react';
import { useMood } from '../../contexts/MoodContext';
import { MOODS, MoodId } from './moods';

/**
 * MoodInsights — an honest weekly summary of check-ins.
 *
 * Only ever shows REAL stored data. If history is off, or there is not enough of
 * it, it says so plainly and invites (never nags) future check-ins — it never
 * fabricates a chart or a trend. Counts come straight from on-device history.
 */
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const MoodInsights: React.FC = () => {
  const { checkIns, preferences, status } = useMood();

  const week = useMemo(() => {
    const since = Date.now() - WEEK_MS;
    const recent = checkIns.filter((c) => new Date(c.createdAt).getTime() >= since);
    const counts = new Map<MoodId, number>();
    recent.forEach((c) => counts.set(c.mood, (counts.get(c.mood) || 0) + 1));
    const max = Math.max(1, ...Array.from(counts.values()));
    return { total: recent.length, counts, max };
  }, [checkIns]);

  // While the first history load is in flight, hold off on the empty state so we
  // don't flash "no check-ins" before the data arrives.
  if (status.loading && checkIns.length === 0) {
    return (
      <div className="mm-insights mm-insights--empty">
        <p className="mm-empty-title">Loading your week…</p>
      </div>
    );
  }

  if (!preferences.saveHistory) {
    return (
      <div className="mm-insights mm-insights--empty">
        <p className="mm-empty-title">Mood history is off</p>
        <p className="mm-empty-sub">Turn on history in Settings to see a simple weekly summary of your check-ins. It stays on this device.</p>
      </div>
    );
  }

  if (week.total === 0) {
    return (
      <div className="mm-insights mm-insights--empty">
        <p className="mm-empty-title">No check-ins yet this week</p>
        <p className="mm-empty-sub">Your weekly summary will appear here as you check in. No pressure — check in whenever it helps.</p>
      </div>
    );
  }

  const rows = MOODS.map((m) => ({ mood: m, count: week.counts.get(m.id) || 0 })).filter((r) => r.count > 0);

  return (
    <div className="mm-insights">
      <p className="mm-insights__lead">
        <strong>{week.total}</strong> check-in{week.total === 1 ? '' : 's'} in the last 7 days
      </p>
      <ul className="mm-insights__bars">
        {rows.map(({ mood, count }) => (
          <li key={mood.id} className="mm-insights__row">
            <span className="mm-insights__name"><span aria-hidden="true">{mood.emoji}</span> {mood.label}</span>
            <span className="mm-insights__track">
              <span
                className="mm-insights__fill"
                style={{ width: `${Math.round((count / week.max) * 100)}%`, background: mood.accent }}
              />
            </span>
            <span className="mm-insights__count">{count}</span>
          </li>
        ))}
      </ul>
      <p className="mm-insights__note">A personal reflection, not an assessment.</p>
    </div>
  );
};

export default MoodInsights;
